import { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/outline';
import type { EmailChangeConfirmationResult } from 'utils/emailChangeConfirmation';

type Props = { confirm: () => Promise<EmailChangeConfirmationResult> };

export function EmailChangeConfirmation({ confirm }: Props) {
  const [result, setResult] = useState<EmailChangeConfirmationResult | null>(
    null,
  );

  useEffect(() => {
    let active = true;
    confirm().then((value) => {
      if (active) setResult(value);
    });
    return () => {
      active = false;
    };
  }, [confirm]);

  const confirmed = result?.status === 'confirmed';
  const actionClasses =
    'inline-flex justify-center rounded-md bg-[#262626] px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2';

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-16 flex items-center justify-center">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <img
          src="/shipaid-email-logo.png"
          alt="ShipAid"
          className="h-auto w-36 mb-8"
        />
        {!result ? (
          <div role="status" className="text-sm text-gray-600">
            Confirming your email change…
          </div>
        ) : confirmed ? (
          <div role="status">
            <CheckCircleIcon
              aria-hidden="true"
              className="h-10 w-10 text-green-600 mb-4"
            />
            <h1 className="text-2xl font-semibold text-gray-900">
              Email address updated
            </h1>
            <p className="mt-3 text-sm text-gray-600">
              Your ShipAid email address is now:
            </p>
            <p className="mt-1 break-words font-semibold text-gray-900">
              {result.email}
            </p>
            <p className="mt-3 text-sm text-gray-600">
              Use this address the next time you sign in.
            </p>
            <a
              href="/settings/account"
              className={`${actionClasses} mt-6 w-full`}
            >
              Continue to account settings
            </a>
          </div>
        ) : (
          <div>
            <div role="alert">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="h-10 w-10 text-amber-600 mb-4"
              />
              <h1 className="text-2xl font-semibold text-gray-900">
                We couldn’t confirm your email change
              </h1>
              <p className="mt-3 text-sm text-gray-600">
                {result.status === 'unavailable'
                  ? 'We couldn’t reach the confirmation service. Check your account to see which email address is saved.'
                  : 'This link may have expired or already been used. Check your account email address, or request a new link from Account settings.'}
              </p>
            </div>
            <a
              href="/settings/account"
              className={`${actionClasses} mt-6 w-full`}
            >
              Check account settings
            </a>
            <a
              href="/login"
              className="mt-4 block text-center text-sm font-medium text-blue-700 hover:underline"
            >
              Go to sign in
            </a>
          </div>
        )}
      </div>
    </main>
  );
}
